import toCurrency from '../../utils/money/toCurrency';

/**
 * The hero amount in the transaction's own currency: a credit (negative on the
 * wire) reads `+$45.99`, a debit `$45.99`. Without a code the amount formats as USD.
 */
export function formatTransactionAmount(amount: string, currencyCode?: string | null): string {
  const value = Number(amount);
  const formatted = toCurrency(Math.abs(value), { currencyCode });
  return value < 0 ? `+${formatted}` : formatted;
}

export interface ITransactionCaptionSource {
  isPending: boolean;
  /** A row the household recorded itself rather than one the bank reported. */
  isUnverified: boolean;
}

/**
 * The muted line under an amount. Pending comes first: a bank row that has not
 * settled is the more useful thing to say, and a manual row never pends.
 */
export function transactionCaption({ isPending, isUnverified }: ITransactionCaptionSource): string | null {
  if (isPending) {
    return 'Pending';
  }
  return isUnverified ? 'Manual bill payment' : null;
}

export interface ITransactionNameSource {
  merchant?: { name: string } | null;
  description: string;
}

/** The merchant's name where there is one, else the transaction's own description. */
export function transactionDisplayName({ merchant, description }: ITransactionNameSource): string {
  return merchant?.name ?? description;
}

/**
 * The footer line: the raw bank description, but only when it says something
 * the display name does not. Compared case- and whitespace-insensitively, so
 * "Starbucks" over "STARBUCKS" earns no footer.
 */
export function transactionFooter(rawDescription: string | null | undefined, displayName: string): string | null {
  if (!rawDescription) {
    return null;
  }
  return normalize(rawDescription) === normalize(displayName) ? null : rawDescription;
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}
